import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, applyApprovedPayment, resolveInvoiceFinancials } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { createAuthFor, syncAuthPassword } from '../../api';
import { generateTempPassword } from '../../core/helpers';
import { buildApiFilter, usePaginatedApiRows } from '../../core/searchFilters';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { buildOrderParam } from '../../core/tableView';
import { SortableTh } from '../../components/SortableTh';

const PAY_ORDER = { date: 'date', clientName: 'client_name', amount: 'amount', mode: 'mode', status: 'status', reference: 'reference' };


function PaymentsPage({ payments, invoices, clients }) {
  const { showToast, setInvoices, setPayments, setClients, user, visibleClients } = useApp();
  const scopedClients = visibleClients || clients;
  const scopedClientIds = new Set(scopedClients.map(c => c.id));
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "date" ? "desc" : "asc"); }
  };
  const MODES = { bank_transfer: "Bank Transfer", upi: "UPI", cheque: "Cheque", cash: "Cash", razorpay: "Razorpay" };
  const clearFilters = () => { setSearch(""); setClientId(""); setMode(""); setFrom(""); setTo(""); setStatusFilter("all"); };

  const approvePayment = async (p) => {
    const invoice = resolveInvoiceFinancials(
      (invoices || []).find(i => i.id === p.invoiceId),
      payments,
    );
    if (!invoice) { showToast("Invoice not found", "error"); return; }
    try {
      await applyApprovedPayment({
        payment: p,
        invoice,
        sb,
        setInvoices,
        setPayments,
        setClients,
      });
      showToast("Payment approved — invoice updated", "success");
    } catch (e) {
      showToast(e.message || "Could not approve payment", "error");
    }
  };

  const localVisible = (payments || []).filter(p => {
    if (user?.role === "employee" && !scopedClientIds.has(p.clientId)) return false;
    if (clientId && p.clientId !== clientId) return false;
    if (mode && p.mode !== mode) return false;
    if (statusFilter === "pending" && p.status !== "pending_approval" && p.status !== "pending_review") return false;
    if (statusFilter === "approved" && p.status !== "approved") return false;
    if (search && !((p.clientName||"").toLowerCase().includes(search.toLowerCase()) || (p.reference||"").toLowerCase().includes(search.toLowerCase()))) return false;
    if (!dateInRange(p.date, from, to)) return false;
    return true;
  });
  const apiFilter = buildApiFilter({
    equals: {
      ...(clientId ? { company_id: clientId } : {}),
      ...(mode ? { mode } : {}),
    },
    from, to,
    dateField: "date",
    search,
    searchFields: ["client_name", "reference"],
    order: buildOrderParam(sortKey, sortDir, PAY_ORDER) || "date.desc",
  });
  const { rows: visible, hasMore, loadMore, loadingMore } = usePaginatedApiRows("payments", apiFilter, localVisible, { enabled: !!apiFilter });
  const total = visible.filter(p => p.status !== "pending_approval").reduce((s, p) => s + p.amount, 0);
  const pendingCount = (payments || []).filter(p => p.status === "pending_approval" || p.status === "pending_review").length;

  return (
    <>
      {pendingCount > 0 && (
        <div style={{ marginBottom: 16, padding: "12px 16px", background: "#FFF8EC", border: "1px solid rgba(201,161,74,.35)", borderRadius: 8, fontSize: 13, display: "flex", alignItems: "center", gap: 10 }}>
          <Icon name="warning" size={16} />
          <span><strong>{pendingCount}</strong> payment{pendingCount > 1 ? "s" : ""} awaiting team approval.</span>
          <button className="btn btn-sm btn-primary" style={{ marginLeft: "auto" }} onClick={() => setStatusFilter("pending")}>Review →</button>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 20 }}>
        {[
          ["Total Received",    INR(total),                "var(--green)"],
          ["No. of Payments",   visible.length,     "var(--blue)" ],
          ["Avg per Payment",   INR(Math.round(total / Math.max(visible.length,1))), "var(--ink)"],
        ].map(([l, v, c]) => (
          <div key={l} className="stat-box"><div className="stat-lbl">{l}</div><div className="stat-val" style={{ fontSize: 20, color: c }}>{v}</div></div>
        ))}
      </div>
      <FilterBar
        search={search} setSearch={setSearch} searchPlaceholder="Search client / reference…"
        clientId={clientId} setClientId={setClientId} clients={scopedClients}
        from={from} setFrom={setFrom} to={to} setTo={setTo} onClear={clearFilters}
        extra={
          <>
            <select className="f-select" style={{ width: 150 }} value={mode} onChange={e => setMode(e.target.value)}>
              <option value="">All modes</option>
              {Object.entries(MODES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="f-select" style={{ width: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="pending">Pending approval</option>
              <option value="approved">Approved</option>
            </select>
          </>
        } />
      <div className="card">
        <div className="tbl-wrap tbl-m-payments">
          <table>
            <thead><tr>
              <SortableTh label="Date" sortKey="date" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Client" sortKey="clientName" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Amount" sortKey="amount" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Mode" sortKey="mode" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Status" sortKey="status" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Reference" sortKey="reference" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <th>Notes</th><th></th>
            </tr></thead>
            <tbody>
              {visible.length === 0 && <tr><td colSpan={8}><div className="empty"><div className="empty-icon"><Icon name="money" size={30} /></div><div>No payments recorded yet</div></div></td></tr>}
              {visible.map(p => (
                <tr key={p.id}>
                  <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{p.date}</td>
                  <td style={{ fontSize: 13, fontWeight: 500 }}>{p.clientName}</td>
                  <td style={{ fontSize: 14, fontWeight: 700, color: "var(--green)" }}>{INR(p.amount)}</td>
                  <td><span className="tag">{MODES[p.mode] || p.mode}</span></td>
                  <td><Badge status={p.status === "pending_approval" ? "pending_review" : "paid"} /></td>
                  <td style={{ fontSize: 12, color: "var(--muted)", fontFamily: "monospace" }}>{p.reference || "—"}</td>
                  <td style={{ fontSize: 12, color: "var(--muted)" }}>{p.notes || "—"}</td>
                  <td>
                    {p.status === "pending_approval" && (user?.role === "admin" || user?.role === "manager" || user?.role === "employee") && (
                      <button className="btn btn-sm btn-primary" onClick={() => approvePayment(p)}>
                        <InlineIcon name="check" size={13} gap={5}>Approve</InlineIcon>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <LoadMoreBar hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMore} count={visible.length} />
      </div>
    </>
  );
}

// ─── KRAYA SETTINGS PAGE ──────────────────────────────────────────────



export { PaymentsPage };
